import PropTypes from 'prop-types';
import { Phone, MapPin, Globe, Bookmark } from 'lucide-react';
import { Text, Stack } from '@/components/ui/index.js';
import styles from './QuickActions.module.css';

/**
 * QuickActions - Row of quick action buttons for venue
 * Call, Directions, Website, Save
 */
export function QuickActions({
  phone,
  website,
  coordinates,
  onCall,
  onDirections,
  onWebsite,
  onSave,
  isSaved,
}) {
  const actions = [
    {
      id: 'call',
      icon: Phone,
      label: 'Call',
      onClick: onCall,
      available: !!phone,
    },
    {
      id: 'directions',
      icon: MapPin,
      label: 'Directions',
      onClick: onDirections,
      available: !!coordinates,
    },
    {
      id: 'website',
      icon: Globe,
      label: 'Website',
      onClick: onWebsite,
      available: !!website,
    },
    {
      id: 'save',
      icon: Bookmark,
      label: isSaved ? 'Saved' : 'Save',
      onClick: onSave,
      available: true,
      active: isSaved,
    },
  ];
  
  return (
    <div className={styles.container}>
      <Stack direction="row" gap={3} justify="space-between" wrap>
        {actions.map((action) => (
          <ActionButton
            key={action.id}
            action={action}
          />
        ))}
      </Stack>
    </div>
  );
}

/**
 * Individual action button
 */
function ActionButton({ action }) {
  const { icon: Icon, label, onClick, available, active } = action;
  
  if (!available) {
    return (
      <div className={`${styles.button} ${styles.disabled}`}>
        <div className={styles.iconWrapper}>
          <Icon size={20} />
        </div>
        <Text size="sm" color="muted">{label}</Text>
      </div>
    );
  }
  
  return (
    <button
      onClick={onClick}
      className={`${styles.button} ${active ? styles.active : ''}`}
    >
      <div className={`${styles.iconWrapper} ${active ? styles.iconActive : ''}`}>
        <Icon 
          size={20} 
          fill={active ? 'currentColor' : 'none'}
        />
      </div>
      <Text size="sm" color={active ? 'primary' : 'secondary'}>
        {label}
      </Text>
    </button>
  );
}

QuickActions.propTypes = {
  phone: PropTypes.string,
  website: PropTypes.string,
  coordinates: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  onCall: PropTypes.func.isRequired,
  onDirections: PropTypes.func.isRequired,
  onWebsite: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isSaved: PropTypes.bool.isRequired,
};

ActionButton.propTypes = {
  action: PropTypes.shape({
    id: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    available: PropTypes.bool.isRequired,
    active: PropTypes.bool,
  }).isRequired,
};

export default QuickActions;
